import React, {
  useContext,
  useState
} from 'react';

import {
  ELSButton,
  ELSIcon
} from '../../components/els.components';
import { ELSInlineLoader } from '@els/els-ui-common-react';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';
import IsRender from '../../components/is-render/IsRender.component';
import {
  NewRelicAnalyticsAction,
} from '../../models/new-relic-analytics.models';
import {
  ChatEntryDto,
  FollowUpQuestionDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { EntrySubmitProps } from './ai-chat.models';
import { trackNewRelicAction } from '../../components/app/app-state-actions.utilities';
import { AppContext } from '../../components/app/AppProvider.component';

export type AiChatFollowUpQueriesProps = {
  botEntry: ChatEntryDto;
  followUpQuestions: FollowUpQuestionDto[];
  handleSubmit: (props: EntrySubmitProps) => void;
  isFollowUpQuestionsLoading: boolean;
  initiallyCollapsed?: boolean;
}

export type AiChatFollowUpQueriesState = {
  isFollowUpQueriesActive: boolean;
}

const defaultState: AiChatFollowUpQueriesState = {
  isFollowUpQueriesActive: true
};

export const AiChatFollowUpQueries = (props: AiChatFollowUpQueriesProps): JSX.Element => {

  const {
    appState,
    appStateRef
  } = useContext(AppContext);

  const {
    botEntry,
    followUpQuestions,
    handleSubmit,
    isFollowUpQuestionsLoading,
    initiallyCollapsed
  } = props;

  const [state, setState] = useState<AiChatFollowUpQueriesState>({
    ...defaultState,
    isFollowUpQueriesActive: initiallyCollapsed ? false : defaultState.isFollowUpQueriesActive
  });

  const hasFollowUpQuestions = followUpQuestions?.length > 0 && followUpQuestions.some((followUpQuestion) => {
    return followUpQuestion.entryId === botEntry.id;
  });

  if (
    !isFollowUpQuestionsLoading
    && (!hasFollowUpQuestions || !botEntry || botEntry.errorMessage)
  ) {
    return null;
  }

  const handleFollowUpQueriesExpandToggle = () => {
    setState({
      ...state,
      isFollowUpQueriesActive: !state.isFollowUpQueriesActive
    });
  };

  const handleClick = (followUpQuestion: FollowUpQuestionDto) => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_FOLLOW_UP_QUERY_SUBMIT,
        props: {
          entryId: botEntry.id,
          idx: botEntry.index,
          chatUserRole: appState.chatUserRole
        }
      }
    });

    handleSubmit({
      prompt: followUpQuestion.question,
      followUpQuestion,
      isNewChat: false,
      isAppLinkQuery: false,
      isPromptExample: false,
      chatMode: appStateRef.current.chatModeType
    });
  };

  return (
    <div className="u-els-margin-top-1o2">
      <button
        type="button"
        className="u-els-debuttonize u-els-display-block u-els-width-1o1 u-els-color-secondary"
        onClick={handleFollowUpQueriesExpandToggle}
        aria-expanded={state.isFollowUpQueriesActive ? 'true' : 'false'}
      >
        <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE]}>
          <FlexItem>
            <ELSIcon
              size="1x"
              prefix="hmds"
              name={state.isFollowUpQueriesActive ? 'chevron-up' : 'chevron-down'}
              customClass="u-els-display-block"
            />
          </FlexItem>
          <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
            Follow-up questions
          </FlexItem>
        </FlexLayout>
      </button>
      <IsRender isRender={state.isFollowUpQueriesActive}>
        <div className="u-els-margin-top">
          {isFollowUpQuestionsLoading && !hasFollowUpQuestions && (
            <div className="u-els-margin-bottom">
              <ELSInlineLoader />
            </div>
          )}
          {hasFollowUpQuestions && (
            <FlexLayout modifiers={[
              FlexLayoutModifier.COLUMN,
              FlexLayoutModifier.TOP
            ]}>
              {followUpQuestions.map((followUpQuestion) => {
                return (
                  <FlexItem key={followUpQuestion.id}>
                    <ELSButton type={ELSButtonType.PRIMARY}
                      size={ELSButtonSize.SMALL}
                      onClick={() => handleClick(followUpQuestion)}
                      className="c-els-ai-chat__follow-up-query">
                      {followUpQuestion.question}
                    </ELSButton>
                  </FlexItem>
                );
              })}
            </FlexLayout>
          )}
        </div>
      </IsRender>
    </div>
  );
};
